import { useState, useEffect, useCallback, useRef } from 'react';
import { ticketsFilaRetorno } from './mysuiteApi';
import {
  ordenarPorPrazo, aAvisar, agendamentosExcedidos, numeroDoTicket, identidadeDoAviso,
} from './filaRetorno';
import { carregarAvisos, salvarAvisos, limparAntigos } from './avisosRetorno';
import { janela, ehDesktop } from './plataforma';

// ─────────────────────────────────────────────────────────────────────────────
// Fila de retorno — a orquestração.
//
// O serviço (filaRetorno.js) sabe calcular prazos; a API sabe buscar a lista.
// Este hook cuida do ciclo: consulta de tempos em tempos, reavalia os horários
// com frequência e decide quando o operador precisa ser avisado.
//
// POR QUE DOIS RELÓGIOS
//
// Consultar o MySuite a cada 30 segundos seria mau uso do endpoint, e não traria
// nada de novo: a lista de agendamentos muda devagar. O que muda depressa é a
// HORA — um ticket marcado para as 14:30 vira "iminente" sozinho, sem nenhuma
// mudança no servidor.
//
// Então há dois ritmos: a lista é buscada a cada cinco minutos — o mesmo passo
// das outras filas, ver useFilasMysuite.js — e os prazos são recalculados sobre
// a lista em memória a cada meio minuto. Assim o aviso sai na hora certa sem
// martelar a API.
//
// ─────────────────────────────────────────────────────────────────────────────
// UM ALERTA POR TICKET
//
// O modal do agendamento sai UMA VEZ por compromisso e não volta. Antes ele
// reabria a cada três minutos enquanto o ticket estivesse vencido, e o efeito
// era o contrário do pretendido: o operador passava a fechá-lo no reflexo.
//
// Depois desse alerta único o ticket não some — ele continua no cartão da tela
// de início, em "Agendamento excedido", enquanto mantiver a MESMA data de
// previsão e o MESMO status de fila de retorno. Reagendou? Vira compromisso
// novo e tem direito a um alerta novo. Saiu do status 5? Sai da lista inteira.
//
// O registro do que já foi avisado mora em disco (services/avisosRetorno.js),
// e não numa `ref`: em memória, recarregar a página ressuscitava todos os
// alertas já dispensados.
// ─────────────────────────────────────────────────────────────────────────────

/** De quanto em quanto tempo a lista é buscada no MySuite. */
const INTERVALO_CONSULTA_MS = 5 * 60 * 1000;

/** De quanto em quanto tempo os prazos são reavaliados (só conta local). */
const INTERVALO_RELOGIO_MS = 30 * 1000;

export function useFilaRetorno({ operador, avisar }) {
  const [tickets, setTickets] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [alerta, setAlerta] = useState(null);
  const [agora, setAgora] = useState(() => new Date());
  // Espelha o mapa de avisados em disco. É estado, e não só ref, porque a lista
  // de "agendamento excedido" é calculada a partir dele e precisa repintar
  // quando um alerta acabou de ser dado.
  const [avisados, setAvisados] = useState({});

  const consultando = useRef(false);
  // A mesma informação do estado, para os efeitos lerem o valor do momento sem
  // entrar na lista de dependências (e reiniciar o ciclo a cada aviso).
  const avisadosRef = useRef({});

  const codigo = operador?.codigo || null;

  useEffect(() => { avisadosRef.current = avisados; }, [avisados]);

  // Carrega o registro do disco ao entrar, já sem o que passou da validade.
  useEffect(() => {
    if (!codigo) { setAvisados({}); return; }
    const salvos = limparAntigos(carregarAvisos(codigo));
    setAvisados(salvos);
    salvarAvisos(codigo, salvos);
  }, [codigo]);

  /** Grava as identidades informadas como avisadas — em memória e em disco. */
  const registrarAvisos = useCallback((identidades, instante) => {
    if (!identidades.length) return;
    setAvisados((atual) => {
      const novo = { ...atual };
      identidades.forEach((id) => { novo[id] = instante; });
      salvarAvisos(codigo, novo);
      return novo;
    });
  }, [codigo]);

  const recarregar = useCallback(async () => {
    if (!codigo || consultando.current) return;
    consultando.current = true;
    setCarregando(true);
    try {
      const lista = await ticketsFilaRetorno(codigo);
      // null é "não sei" (consulta falhou): mantém o que já havia, em vez de
      // anunciar que a agenda está limpa.
      if (lista !== null) setTickets(lista);
    } finally {
      consultando.current = false;
      setCarregando(false);
    }
  }, [codigo]);

  // Busca no login e depois de tempos em tempos.
  useEffect(() => {
    if (!codigo) { setTickets([]); return undefined; }
    recarregar();
    const id = setInterval(recarregar, INTERVALO_CONSULTA_MS);
    return () => clearInterval(id);
  }, [codigo, recarregar]);

  // Voltar ao app pede uma consulta na hora — mesma regra das outras filas.
  // Cobre a máquina que dormiu e a janela que passou horas na bandeja: aí o
  // relógio pode ter ficado parado, e um retorno agendado não espera.
  //
  // O relógio interno também é acertado aqui. No navegador, a aba em segundo
  // plano tem os temporizadores agrupados, e o `agora` do hook pode estar
  // minutos atrasado no instante em que o operador volta a olhar a tela.
  useEffect(() => {
    if (!codigo) return undefined;

    const aoVoltar = () => {
      if (document.hidden) return;
      setAgora(new Date());
      recarregar();
    };

    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);
    const pararRestauracao = ehDesktop ? janela.aoRestaurar(recarregar) : null;

    return () => {
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', aoVoltar);
      if (typeof pararRestauracao === 'function') pararRestauracao();
    };
  }, [codigo, recarregar]);

  // O relógio: reavalia os prazos sem falar com a rede.
  useEffect(() => {
    const id = setInterval(() => setAgora(new Date()), INTERVALO_RELOGIO_MS);
    return () => clearInterval(id);
  }, []);

  // Decide o alerta a cada volta do relógio. Um ticket entra aqui uma única vez
  // por agendamento — quem filtra é `aAvisar`, contra o registro em disco.
  useEffect(() => {
    if (!tickets.length) return;

    const pendentes = aAvisar(tickets, agora, avisadosRef.current);
    if (!pendentes.length) return;

    const identidades = pendentes.map(identidadeDoAviso).filter(Boolean);
    registrarAvisos(identidades, agora.getTime());

    // Um alerta de cada vez, pelo mais urgente. Abrir vários empilhados faria o
    // operador fechar tudo no reflexo e perder justamente o que importava.
    const ordenados = ordenarPorPrazo(pendentes, agora);
    setAlerta({ ticket: ordenados[0], demais: ordenados.slice(1) });
  }, [tickets, agora, registrarAvisos]);

  const dispensarAlerta = useCallback(() => setAlerta(null), []);

  /**
   * Marca um ticket como tratado: ele não volta a alertar.
   *
   * Usado quando o operador abre o ticket a partir do alerta ou o atende pelo
   * resumo — insistir depois disso seria ruído. Como a chave do registro é o
   * agendamento inteiro (número + data + hora), marca todas as ocorrências
   * daquele número que estejam na lista atual.
   */
  const marcarTratado = useCallback((numero) => {
    if (numero == null) return;
    const identidades = tickets
      .filter(t => numeroDoTicket(t) === Number(numero))
      .map(identidadeDoAviso)
      .filter(Boolean);
    registrarAvisos(identidades, Date.now());
    setAlerta(null);
  }, [tickets, registrarAvisos]);

  const ordenados = ordenarPorPrazo(tickets, agora);

  return {
    tickets: ordenados,
    /** Vencidos que já receberam o alerta — a lista de "Agendamento excedido". */
    excedidos: agendamentosExcedidos(ordenados, agora, avisados),
    carregando,
    agora,
    alerta,
    dispensarAlerta,
    marcarTratado,
    recarregar,
    avisar,
  };
}
